import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from '../../../../hooks';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import { useMixpanel } from '../../../../hooks/useMixpanel';
import { bucketClientObservedDuration } from '../../../../util/clientObservedGeneration';

const ZORI_PULSE_SCAN_TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'expired',
]);
const ZORI_PULSE_SCAN_POLL_INTERVAL_MS = 5000;

const messages = defineMessages({
  started: {
    id: 'actionPlan.regenerate.pulse.started',
    defaultMessage:
      'Coaching regeneration started. A new coaching snapshot will appear in a few minutes.',
  },
  completed: {
    id: 'actionPlan.regenerate.pulse.completed',
    defaultMessage: 'Coaching regeneration complete.',
  },
  failed: {
    id: 'actionPlan.regenerate.pulse.failed',
    defaultMessage: 'We could not regenerate coaching. Please try again.',
  },
});

type ZoriPulseScanTask = {
  task_id: string;
  status: string;
  child_tasks?: Array<{
    agent: string;
    status: string;
  }>;
};

export type UsePulseCoachRegenerationArgs = {
  patientId: string;
  projectId: string;
  enabled: boolean;
  onCompleted: () => void | Promise<void>;
};

export type UsePulseCoachRegeneration = {
  triggerRegenerate: () => Promise<void>;
  isRegenerating: boolean;
};

/**
 * Invokes a coaching-scoped Zori Pulse scan and reports completion only when
 * its `zori-pulse-coach` child completes successfully.
 */
export const usePulseCoachRegeneration = ({
  patientId,
  projectId,
  enabled,
  onCompleted,
}: UsePulseCoachRegenerationArgs): UsePulseCoachRegeneration => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const apiClient = useApiClient();
  const { track } = useMixpanel();

  const [scanTask, setScanTask] = React.useState<ZoriPulseScanTask | null>(
    null,
  );
  const [isInvoking, setIsInvoking] = React.useState(false);

  // Identifies the scan (if any) this hook instance itself started, and when.
  // A scan resumed from `detectInFlight` never populates this, so its
  // eventual terminal status is never reported as clinician-adoption
  // completion. `emittedCompletionTaskIdRef` guards against repeated
  // terminal observations reporting the same completion twice.
  const localScanRef = React.useRef<{
    taskId: string;
    startedAt: number;
  } | null>(null);
  const emittedCompletionTaskIdRef = React.useRef<string | null>(null);

  const triggerRegenerate = React.useCallback(async () => {
    if (
      !enabled ||
      !patientId ||
      !projectId ||
      isInvoking ||
      (scanTask && !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status))
    ) {
      notify(formatMessage(messages.failed), 'error');
      return;
    }

    const startedAt = Date.now();
    setIsInvoking(true);
    try {
      const response = await apiClient.request(
        'POST /v1/agents-v2/zori-pulse-scan/invoke',
        {
          subject_id: patientId,
          project_id: projectId,
          // Coaching scope is supported by agents; the generated invoke body
          // type has not caught up yet.
          scope: 'coaching',
        } as import('@lifeomic/react-client').ZoriPulseScanInvokeBody,
      );
      localScanRef.current = { taskId: response.data.task_id, startedAt };
      setScanTask({ task_id: response.data.task_id, status: 'pending' });
      notify(formatMessage(messages.started), 'success');
      track('pulse_scan_requested', {
        entryPoint: 'action_plan',
        outcome: 'accepted',
      });
    } catch (err) {
      console.error(err);
      notify(formatMessage(messages.failed), 'error');
      track('pulse_scan_requested', {
        entryPoint: 'action_plan',
        outcome: 'error',
      });
    } finally {
      setIsInvoking(false);
    }
  }, [
    apiClient,
    enabled,
    formatMessage,
    isInvoking,
    notify,
    patientId,
    projectId,
    scanTask,
    track,
  ]);

  React.useEffect(() => {
    if (!scanTask || ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status)) {
      return;
    }

    let isCancelled = false;
    let isPolling = false;

    const poll = async () => {
      if (isPolling) {
        return;
      }
      isPolling = true;
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations/:task_id',
          { task_id: scanTask.task_id },
        );
        if (isCancelled) {
          return;
        }
        const task = response.data as ZoriPulseScanTask;
        if (ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(task.status)) {
          const coachTask = task.child_tasks?.find(
            child => child.agent === 'zori-pulse-coach',
          );
          if (coachTask?.status === 'completed') {
            try {
              await onCompleted();
              if (!isCancelled) {
                notify(formatMessage(messages.completed), 'success');
              }
            } catch {
              if (!isCancelled) {
                notify(formatMessage(messages.failed), 'error');
              }
            }
          } else {
            notify(formatMessage(messages.failed), 'error');
          }
          if (
            localScanRef.current?.taskId === task.task_id &&
            emittedCompletionTaskIdRef.current !== task.task_id
          ) {
            emittedCompletionTaskIdRef.current = task.task_id;
            const outcome: 'completed' | 'failed' | 'expired' =
              coachTask?.status === 'completed' ||
              coachTask?.status === 'expired'
                ? coachTask.status
                : 'failed';
            track('pulse_scan_completed', {
              entryPoint: 'action_plan',
              outcome,
              durationBucket: bucketClientObservedDuration(
                localScanRef.current.startedAt,
              ),
            });
          }
          if (!isCancelled) {
            setScanTask(task);
          }
        } else {
          setScanTask(task);
        }
      } catch {
        if (isCancelled) {
          return;
        }
        setScanTask(null);
        notify(formatMessage(messages.failed), 'error');
      } finally {
        isPolling = false;
      }
    };

    const interval = window.setInterval(poll, ZORI_PULSE_SCAN_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [apiClient, formatMessage, notify, onCompleted, scanTask, track]);

  React.useEffect(() => {
    if (!enabled || !patientId) {
      return;
    }

    let isCancelled = false;

    const detectInFlight = async () => {
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations',
          { subject_id: patientId },
        );
        if (isCancelled) {
          return;
        }
        const inFlight = response.data.find(
          (task: ZoriPulseScanTask) =>
            !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(task.status),
        );
        if (inFlight) {
          setScanTask(
            prev =>
              prev ?? { task_id: inFlight.task_id, status: inFlight.status },
          );
        }
      } catch {
        // Best-effort: ignore failures detecting an existing scan.
      }
    };

    void detectInFlight();

    return () => {
      isCancelled = true;
    };
  }, [apiClient, enabled, patientId]);

  const isRegenerating =
    isInvoking ||
    (!!scanTask && !ZORI_PULSE_SCAN_TERMINAL_STATUSES.has(scanTask.status));

  return { triggerRegenerate, isRegenerating };
};

export default usePulseCoachRegeneration;
